import abstractModel from '../../core/MercatelyModel';
import {
  MERCATELY_API,
  MERCATELY_API_V2
} from '../../constants/AppConstants';

const resource = `${MERCATELY_API}/customers`;
const csrfToken = document.querySelector('[name=csrf-token]')?.content;

const model = abstractModel(resource);

/**
 * Fetches a list of customers from the API.
 *
 * @async
 * @function getCustomers
 * @param {Object} params - Query parameters to filter customers (e.g., search terms, pagination).
 * @returns {Promise<Object>} The API response containing the list of customers.
 */
model.getCustomers = async (params) => {
  const newResource = `${MERCATELY_API_V2}/customers`;

  return model.get(params, { path: newResource });
};

/**
 * Fetches a list of exported customers from the API.
 *
 * @async
 * @function getExportedCustomers
 * @param {Object} params - Query parameters to filter exported customers (e.g., pagination).
 * @returns {Promise<Object>} The API response containing the list of exported customers.
 */
model.getExportedCustomers = async (params) => {
  const newResource = `${MERCATELY_API_V2}/customers/exported_customers`;

  return model.get(params, { path: newResource });
};

/**
 * Initiates the export of customer data by sending a request to the API.
 *
 * @async
 * @function createExport
 * @param {Object} params - Parameters for the export request (e.g., filter options for customers).
 * @returns {Promise<Object>} The API response confirming the export initiation.
 */
model.createExport = async (params) => {
  const newResource = `${MERCATELY_API_V2}/customers/export`;

  return model.save(params, { path: newResource });
};

model.getOne = async (params, queryParams = {}) => model.findById(params.id, queryParams);

model.getCustomerColumns = async () => {
  const newResource = `${MERCATELY_API}/whatsapp_templates/customer_columns`;

  return model.get({}, { path: newResource });
};

model.createCustomerFromDirectMessage = async (params) => {
  const newResource = `${resource}/create_from_direct_message`;

  return model.save(params, { path: newResource });
};

model.getForEdit = async (params) => {
  const newResource = `${resource}/${params.id}/edit`;

  return model.get({}, { path: newResource });
};

model.createFromRetailer = async (params) => {
  const newResource = `${resource}/create_from_retailer`;

  return model.save(params, { path: newResource });
};

model.updateFromRetailer = async (params) => {
  const newResource = `${resource}/update_from_retailer`;

  return model.save(params, { path: newResource });
};

model.updatePhoneNumber = async (params) => {
  const newResource = `${resource}/update_phone_number`;

  return model.save(params, { path: newResource });
};

model.onlyInfo = async (params) => {
  const newResource = `${resource}/only_info/${params.web_id}`;

  return model.get({}, { path: newResource });
};

model.customerFields = async (params) => {
  const newResource = `${resource}/${params.id}/custom_fields`;

  return model.get({}, { path: newResource });
};

model.getEvents = async (params) => {
  const newResource = `${MERCATELY_API}/customer_events/events`;

  return model.get(params, { path: newResource });
};

model.searchAssignedCustomers = async (params) => {
  const newResource = `${MERCATELY_API}/bsp/voice/search_assigned_customers`;

  return model.get(params, { path: newResource });
};

model.delete = async (params) => model.remove(params, { path: `${MERCATELY_API_V2}/customers` });

model.getDocuments = async ({ customerId }) => {
  const path = `/retailers/${ENV.SLUG}/customers/${customerId}/documents`;
  return model.get({}, { path });
};

model.uploadDocument = async ({ customerId, file }) => {
  const path = `/retailers/${ENV.SLUG}/customers/${customerId}/documents`;
  const formData = new FormData();
  formData.append('file', file);

  return model.post(formData, { path, csrfToken });
};

model.deleteDocument = async ({ customerId, documentId }) => {
  const path = `/retailers/${ENV.SLUG}/customers/${customerId}/documents/${documentId}`;
  return model.remove({}, { path, csrfToken });
};

export default model;
